import { useState } from 'react';
import { updateAndReload, type UpdateReloadOutcome } from '../lib/sw-update';
import { useUpdateRequiredStore } from '../lib/client-version';

const OUTCOME_MESSAGES: Record<Exclude<UpdateReloadOutcome, 'reloading'>, string> = {
  offline: 'You appear to be offline. Connect to the internet and try again.',
  'still-outdated':
    'Still out of date after reloading. Close every ClinikChat tab and window, then reopen ClinikChat. If this continues, the new version may not have finished deploying yet.',
  failed:
    'Could not remove the old version automatically. Close every ClinikChat tab and window, then reopen ClinikChat.',
};

/**
 * Blocking full-screen notice shown once the server has rejected this bundle
 * as older than the configured minimum (426 CLIENT_UPDATE_REQUIRED).
 */
export default function UpdateRequiredScreen() {
  const minVersion = useUpdateRequiredStore((s) => s.minVersion);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<UpdateReloadOutcome | null>(null);

  const onUpdate = () => {
    setBusy(true);
    setOutcome(null);
    void updateAndReload()
      .then(setOutcome)
      .catch(() => setOutcome('failed'))
      .finally(() => setBusy(false));
  };

  return (
    <main
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="update-required-title"
      aria-describedby="update-required-body"
      className="flex min-h-screen w-full items-center justify-center bg-slate-50 px-4 py-8 dark:bg-slate-900"
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-lg dark:bg-slate-800 sm:p-8">
        <h1
          id="update-required-title"
          className="text-xl font-semibold text-slate-900 dark:text-white"
        >
          Update required
        </h1>
        <p id="update-required-body" className="mt-3 text-slate-600 dark:text-slate-300">
          This version of ClinikChat is out of date and can no longer connect. Update to the latest
          version to continue.
          {minVersion ? ` Minimum supported version: ${minVersion}.` : ''}
        </p>
        {outcome && outcome !== 'reloading' ? (
          <p role="status" className="mt-4 text-sm text-slate-600 dark:text-slate-300">
            {OUTCOME_MESSAGES[outcome]}
          </p>
        ) : null}
        <button
          type="button"
          onClick={onUpdate}
          disabled={busy}
          autoFocus
          className="mt-6 min-h-[44px] w-full rounded-lg bg-slate-900 px-4 py-2 font-semibold text-white disabled:opacity-60 dark:bg-white dark:text-slate-900"
        >
          {busy ? 'Updating…' : 'Update now'}
        </button>
      </div>
    </main>
  );
}
